/**
 * LocationsToggleFilters — compact select for filtering the location list
 * by the CRM Automation master toggle.
 *
 * Renders a single select (All / ON / OFF). Designed to sit next to the
 * TimeRangeFilter in the page header. URL state is owned by
 * `useUrlLocationFilters` — this component is presentational only.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { LocationFilters, ToggleFilter } from '@/hooks/useUrlLocationFilters'

interface Props {
  readonly filters: LocationFilters
  readonly onChange: (next: Partial<LocationFilters>) => void
  /**
   * Counts surfaced inline so operators can see how many locations match
   * each option before they commit the filter. NULL when data isn't loaded
   * yet — the select still renders without counts.
   */
  readonly counts?: {
    readonly crmOn: number
    readonly crmOff: number
    readonly total: number
  } | null
}

function formatOption(label: string, count: number | undefined): string {
  return typeof count === 'number' ? `${label} (${count})` : label
}

export function LocationsToggleFilters({ filters, onChange, counts }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
          CRM Automation
        </span>
        <Select
          value={filters.crm}
          onValueChange={(v: ToggleFilter) => onChange({ crm: v })}
        >
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{formatOption('All', counts?.total)}</SelectItem>
            <SelectItem value="on">{formatOption('On', counts?.crmOn)}</SelectItem>
            <SelectItem value="off">{formatOption('Off', counts?.crmOff)}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
